import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { DespachoInfoCallout, DespachoSectionCard } from "@/components/despacho/DespachoUi";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { despachoFiscalInfoApi, parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";
import { isDespachoReadOnly } from "@/modules/despacho/despachoPermissions";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";

/**
 * Interruptores de alertas/recordatorios que el sistema genera solo.
 * No incluye plantillas de Recordatorios internos (CRUD propio).
 */
export default function CalendarioAutoAlertsPanel() {
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await despachoFiscalInfoApi.getAutoAlertSettings();
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(parseFiscalInfoError(err) || "No se pudieron cargar los interruptores");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const internalItems = useMemo(
    () => items.filter((row) => row.scope === "internal"),
    [items],
  );
  const clientItems = useMemo(
    () => items.filter((row) => row.scope === "client"),
    [items],
  );

  const toggleItem = async (row, nextEnabled) => {
    if (readOnly) return;
    setSavingId(row.id);
    try {
      const enabled = {};
      items.forEach((it) => {
        enabled[it.id] = it.id === row.id ? Boolean(nextEnabled) : Boolean(it.enabled);
      });
      const data = await despachoFiscalInfoApi.putAutoAlertSettings({ enabled });
      setItems(Array.isArray(data?.items) ? data.items : []);
      const cancelled = Number(data?.cancelled_pending || 0);
      toast.success(
        nextEnabled
          ? "Alerta automática reactivada"
          : cancelled
            ? `Alerta pausada · ${cancelled} pendiente(s) cancelada(s)`
            : "Alerta automática pausada",
      );
    } catch (err) {
      toast.error(parseFiscalInfoError(err) || "No se pudo guardar");
    } finally {
      setSavingId("");
    }
  };

  const renderList = (rows, emptyLabel) => {
    if (loading) {
      return <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</p>;
    }
    if (!rows.length) {
      return <p className="text-sm text-slate-500 m-0">{emptyLabel}</p>;
    }
    return (
      <ul className="space-y-2 m-0 p-0 list-none" data-testid="calendario-auto-alerts-list">
        {rows.map((row) => (
          <li
            key={row.id}
            className="rounded-sm border border-slate-200 bg-white px-3 py-3 flex items-start justify-between gap-3"
            data-testid={`calendario-auto-alert-${row.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 m-0">{row.label}</p>
              <p className="text-[11px] text-slate-500 m-0 mt-1 leading-relaxed">{row.description}</p>
              {row.channel_ready === false ? (
                <p className="text-[10px] text-amber-800 m-0 mt-1">
                  Canal de envío a cliente aún en preparación (WhatsApp / correo).
                </p>
              ) : null}
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1">
              <Switch
                checked={Boolean(row.enabled)}
                disabled={readOnly || savingId === row.id}
                onCheckedChange={(v) => toggleItem(row, v)}
                data-testid={`calendario-auto-alert-switch-${row.id}`}
              />
              <span className="text-[10px] text-slate-500">
                {row.enabled ? "Encendido" : "Apagado"}
              </span>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="space-y-4" data-testid="calendario-auto-alerts-panel">
      <DespachoInfoCallout>
        Solo alertas que el sistema <strong>genera y envía solo</strong> (vencimientos/escalamientos).
        Los recordatorios operativos de <strong>Recordatorios internos</strong> se siguen controlando en su pestaña.
        El tablero de precarga (declaraciones y actividades) está en la subpestaña{" "}
        <strong>Recordatorios programados</strong>. No afecta Robot SAT ni descargas XML.
      </DespachoInfoCallout>

      <Tabs defaultValue="internal" className="space-y-3">
        <TabsList className="rounded-sm h-9">
          <TabsTrigger value="internal" className="rounded-sm text-xs" data-testid="calendario-auto-alerts-tab-internal">
            Internas
          </TabsTrigger>
          <TabsTrigger value="client" className="rounded-sm text-xs" data-testid="calendario-auto-alerts-tab-client">
            Clientes
          </TabsTrigger>
        </TabsList>
        <TabsContent value="internal" className="mt-0">
          <DespachoSectionCard
            title="Alertas internas automáticas"
            description="Apague temporalmente avisos excesivos o durante mantenimiento / correcciones."
          >
            {renderList(internalItems, "Sin alertas internas automáticas.")}
          </DespachoSectionCard>
        </TabsContent>
        <TabsContent value="client" className="mt-0">
          <DespachoSectionCard
            title="Alertas / sugerencias a clientes"
            description="Preparadas para cuando el canal WhatsApp negocios y correo estén activos."
          >
            {renderList(clientItems, "Sin alertas a clientes configuradas.")}
          </DespachoSectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
